import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useForms } from "../../hooks/use-active-forms";
import { useCoupons } from "../../hooks/use-active-coupons";
import { Form as FormI } from "@/pages/forms/types/form";
import { Coupon } from "@/pages/coupons/types/coupon";
import { checkSlugAvailability } from "@/features/payment-pages/services/check-slug-availability";
import { buildPaymentUrl } from "@/features/payment-pages/utils/pay-url";
import { BundleWizardData } from "./wizard-form";

type SlugStatus = "idle" | "checking" | "available" | "taken";

export default function StepLinks({
  form,
  createPaymentPage,
  setCreatePaymentPage,
  paymentSlug,
  setPaymentSlug,
}: {
  form: UseFormReturn<BundleWizardData>;
  createPaymentPage: boolean;
  setCreatePaymentPage: (v: boolean) => void;
  paymentSlug: string;
  setPaymentSlug: (v: string) => void;
}) {
  const { data: formsData } = useForms();
  const { data: couponsData } = useCoupons();
  const forms = formsData?.forms || [];
  const coupons = couponsData?.coupons || [];

  // Live slug availability (same check the payment-page wizard uses), debounced.
  const [slugStatus, setSlugStatus] = useState<SlugStatus>("idle");
  useEffect(() => {
    const slug = paymentSlug.trim();
    if (!createPaymentPage || !slug) {
      setSlugStatus("idle");
      return;
    }
    setSlugStatus("checking");
    const t = setTimeout(async () => {
      try {
        const available = await checkSlugAvailability(slug);
        setSlugStatus(available ? "available" : "taken");
      } catch {
        setSlugStatus("idle");
      }
    }, 400);
    return () => clearTimeout(t);
  }, [paymentSlug, createPaymentPage]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="user_payment_type"
          render={({ field }) => (
            <FormItem>
              <FormLabel>User Payment Type</FormLabel>
              <Select
                value={field.value ?? "NONE"}
                onValueChange={(value) =>
                  field.onChange(value === "NONE" ? null : value)
                }
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="NONE">None</SelectItem>
                  <SelectItem value="NEW_USER">New User</SelectItem>
                  <SelectItem value="OLD_USER">Old User</SelectItem>
                  <SelectItem value="ANY">Any</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="company_id"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Company ID (optional)</FormLabel>
              <FormControl>
                <Input
                  placeholder="Company ID"
                  {...field}
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="form_ids"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Forms</FormLabel>
              <FormControl>
                <div className="space-y-2">
                  <Select
                    onValueChange={(value) => {
                      const current = field.value || [];
                      if (!current.includes(value)) {
                        field.onChange([...current, value]);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Forms" />
                    </SelectTrigger>
                    <SelectContent>
                      {forms.map((f: FormI) => (
                        <SelectItem key={f.id} value={f.id}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-2">
                    {field.value?.map((id) => {
                      const f = forms.find((x: FormI) => x.id === id);
                      return (
                        f && (
                          <span
                            key={id}
                            className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm"
                          >
                            {f.name}
                            <button
                              type="button"
                              onClick={() =>
                                field.onChange(
                                  field.value.filter((x) => x !== id),
                                )
                              }
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </span>
                        )
                      );
                    })}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="coupon_ids"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Coupons</FormLabel>
              <FormControl>
                <div className="space-y-2">
                  <Select
                    onValueChange={(value) => {
                      const current = field.value || [];
                      if (!current.includes(value)) {
                        field.onChange([...current, value]);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Coupons" />
                    </SelectTrigger>
                    <SelectContent>
                      {coupons.map((c: Coupon) => (
                        <SelectItem key={c.id} value={c.id}>
                          {c.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex flex-wrap gap-2">
                    {field.value?.map((id) => {
                      const c = coupons.find((x: Coupon) => x.id === id);
                      return (
                        c && (
                          <span
                            key={id}
                            className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm"
                          >
                            {c.name}
                            <button
                              type="button"
                              onClick={() =>
                                field.onChange(
                                  field.value.filter((x) => x !== id),
                                )
                              }
                              className="text-red-500 hover:text-red-700"
                            >
                              ×
                            </button>
                          </span>
                        )
                      );
                    })}
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="flex items-center justify-between border rounded-md p-3">
        <div>
          <p className="text-sm font-medium">Create a payment page next</p>
          <p className="text-xs text-gray-500">
            After saving, jump straight to payment-page creation with this
            offering preselected.
          </p>
        </div>
        <Switch
          checked={createPaymentPage}
          onCheckedChange={setCreatePaymentPage}
        />
      </div>

      {createPaymentPage ? (
        <FormItem>
          <FormLabel>
            Payment Slug <span className="text-red-500">*</span>
          </FormLabel>
          <Input
            placeholder="e.g. therapy-4-session"
            value={paymentSlug}
            onChange={(e) => {
              const slug = e.target.value.toLowerCase();
              setPaymentSlug(slug);
              // The full payment URL is what gets stored on the bundle.
              form.setValue(
                "payment_page_link",
                slug ? buildPaymentUrl(slug) : "",
              );
            }}
          />
          {paymentSlug.trim() && (
            <p className="text-xs text-gray-500">
              Payment URL:{" "}
              <span className="font-mono">{buildPaymentUrl(paymentSlug)}</span>
            </p>
          )}
          {slugStatus === "checking" && (
            <p className="text-xs text-gray-400">Checking availability…</p>
          )}
          {slugStatus === "available" && (
            <p className="text-xs text-green-600">Slug available</p>
          )}
          {slugStatus === "taken" && (
            <p className="text-xs text-red-600">Slug already exists</p>
          )}
        </FormItem>
      ) : (
        <FormField
          control={form.control}
          name="payment_page_link"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Payment Page Link <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input placeholder="https://..." {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
